import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { getAuth } from "@/src/core/auth/session";
import { getDeviceId } from "@/src/core/device";
import {
  BASE_URL,
  CONVERSATION_API_URL,
  OS_PLATFORM_WEB,
  PYTHON_SERVICE_URL,
  SEARCH_SERVICE_URL,
} from "@/src/core/config/app";
import { ApiError } from "./types";

/** Backends the web app talks to. */
const services = {
  laravel: BASE_URL,
  python: PYTHON_SERVICE_URL,
  conversation: CONVERSATION_API_URL,
  search: SEARCH_SERVICE_URL,
} as const;

/** Auth and device headers, added per request so a fresh login is picked up. */
export const withAuthHeaders = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const { id, token } = getAuth();
  if (token) config.headers.set("Authorization", token);
  if (id) config.headers.set("id", String(id));
  const deviceId = getDeviceId();
  if (deviceId) config.headers.set("X-Device-ID", deviceId);
  return config;
};

/**
 * Turns any axios failure into an ApiError carrying the backend's own message:
 * Laravel's `message`, or FastAPI's `detail` when it is a string (the Python
 * service answers errors as `{"detail": "..."}`).
 */
export const toApiError = (
  error: AxiosError<{ message?: string; detail?: unknown }>,
): ApiError =>
  new ApiError(
    error.response?.status ?? 0,
    // A connect timeout (Node AggregateError) has an empty message; fall back to the code.
    error.response?.data?.message ||
      (typeof error.response?.data?.detail === "string"
        ? error.response.data.detail
        : "") ||
      error.message ||
      error.code ||
      "Network error",
    error.response?.data,
  );

const createClient = (baseURL: string | undefined): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      Accept: "application/json",
      "X-OS-Platform": String(OS_PLATFORM_WEB),
    },
  });
  instance.interceptors.request.use(withAuthHeaders);
  instance.interceptors.response.use(undefined, (error: AxiosError) =>
    Promise.reject(toApiError(error as AxiosError<{ message?: string }>)),
  );
  return instance;
};

/**
 * HTTP clients for feature `api/` files. They throw an ApiError on failure, so
 * TanStack Query's `isError` / `error` work.
 */
export const api = {
  laravel: createClient(services.laravel),
  python: createClient(services.python),
  conversation: createClient(services.conversation),
  search: createClient(services.search),
  /** Absolute URLs outside our backends (e.g. the company-details JSON). */
  external: createClient(undefined),
};
